import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../config/api';
import {
  XMarkIcon,
  ArrowsRightLeftIcon,
  SparklesIcon,
  ClockIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface SwapMealModalProps {
  planId: number;
  entry: any;
  onClose: () => void;
  onSwapComplete: () => void;
  coachMode?: boolean;
  clientId?: number;
}

export const SwapMealModal: React.FC<SwapMealModalProps> = ({
  planId,
  entry,
  onClose,
  onSwapComplete,
  coachMode,
  clientId,
}) => {
  const [selectedAlternative, setSelectedAlternative] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Parse current meal data
  const currentMealData = typeof entry.custom_meal_data === 'string'
    ? JSON.parse(entry.custom_meal_data)
    : entry.custom_meal_data;

  const currentMealName = entry.custom_meal_name || currentMealData?.name || 'Current Meal';

  // Fetch alternatives
  const { data: alternativesData, isLoading, error, refetch } = useQuery({
    queryKey: coachMode ? ['meal-alternatives', planId, entry.id, 'client', clientId] : ['meal-alternatives', planId, entry.id],
    queryFn: () => apiClient.getSwapAlternatives(planId, entry.id, 3, coachMode ? clientId : undefined),
    enabled: !!entry.id,
  });

  // Swap mutation
  const swapMutation = useMutation({
    mutationFn: async (alternativeIndex: number) => {
      const alternative = alternativesData?.alternatives?.[alternativeIndex];
      if (!alternative) throw new Error('Alternative not found');

      // Update the entry with the new meal data
      return apiClient.updateMealEntry(planId, entry.id, {
        custom_meal_name: alternative.name,
        custom_meal_data: JSON.stringify(alternative),
        nutrition: alternative.nutrition,
        prep_time_minutes: alternative.prepTime,
        cook_time_minutes: alternative.cookTime,
        therapeutic_notes: alternative.therapeuticNotes,
      }, coachMode ? clientId : undefined);
    },
    onSuccess: () => {
      // Log the swap for learning
      apiClient.submitMealFeedback(planId, {
        entryId: entry.id,
        feedbackType: 'swap',
        feedbackData: {
          original: currentMealName,
          swappedTo: alternativesData?.alternatives?.[selectedAlternative!]?.name,
        },
      }, coachMode ? clientId : undefined).catch(console.error); // Non-blocking

      queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plan', planId, 'client', clientId] : ['meal-plan', planId] });
      onSwapComplete();
    },
  });

  const handleSwap = () => {
    if (selectedAlternative !== null) {
      swapMutation.mutate(selectedAlternative);
    }
  };

  const alternatives = alternativesData?.alternatives || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ArrowsRightLeftIcon className="h-6 w-6 text-white" />
              <h2 className="text-lg font-semibold text-white">Swap Meal</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Current Meal */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <p className="text-xs text-gray-500 mb-1">Currently planned:</p>
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">{currentMealName}</h3>
            <div className="text-sm text-gray-500">
              {Math.round(entry.nutrition?.calories || 0)} cal
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              <p className="mt-4 text-gray-500">Generating alternatives...</p>
              <p className="text-xs text-gray-400 mt-1">
                Finding meals that match your preferences and nutritional needs
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">Failed to generate alternatives</p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Try again
              </button>
            </div>
          ) : alternatives.length === 0 ? (
            <div className="text-center py-8">
              <SparklesIcon className="h-10 w-10 mx-auto text-gray-400" />
              <p className="mt-4 text-gray-500">No alternatives available</p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Generate new options
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                <SparklesIcon className="h-4 w-4 inline mr-1 text-blue-500" />
                AI-generated alternatives based on your preferences:
              </p>

              {alternatives.map((alt: any, index: number) => (
                <div
                  key={index}
                  onClick={() => setSelectedAlternative(index)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedAlternative === index
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{alt.name}</h4>
                      {alt.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {alt.description}
                        </p>
                      )}
                    </div>
                    {selectedAlternative === index && (
                      <CheckIcon className="h-5 w-5 text-blue-600 flex-shrink-0 ml-2" />
                    )}
                  </div>

                  {/* Nutrition */}
                  <div className="mt-3 flex items-center gap-4 text-xs">
                    <span className="text-gray-600">
                      {Math.round(alt.nutrition?.calories || 0)} cal
                    </span>
                    <span className="text-blue-600">
                      {Math.round(alt.nutrition?.protein || 0)}g protein
                    </span>
                    <span className="text-amber-600">
                      {Math.round(alt.nutrition?.carbs || 0)}g carbs
                    </span>
                    <span className="text-rose-600">
                      {Math.round(alt.nutrition?.fat || 0)}g fat
                    </span>
                    {(alt.prepTime || alt.cookTime) && (
                      <span className="flex items-center gap-1 text-gray-500">
                        <ClockIcon className="h-3 w-3" />
                        {(alt.prepTime || 0) + (alt.cookTime || 0)} min
                      </span>
                    )}
                  </div>

                  {/* Ingredients preview */}
                  {alt.ingredients && alt.ingredients.length > 0 && (
                    <div className="mt-2 text-xs text-gray-400">
                      Ingredients: {alt.ingredients.slice(0, 5).map((i: any) => i.name).join(', ')}
                      {alt.ingredients.length > 5 && '...'}
                    </div>
                  )}

                  {/* Therapeutic notes */}
                  {alt.therapeuticNotes && (
                    <div className="mt-2 text-xs text-teal-600 bg-teal-50 rounded px-2 py-1">
                      {alt.therapeuticNotes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <SparklesIcon className="h-4 w-4" />
              Generate more options
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSwap}
                disabled={selectedAlternative === null || swapMutation.isPending}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  selectedAlternative === null
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {swapMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block mr-2" />
                    Swapping...
                  </>
                ) : (
                  'Swap Meal'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
